import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { DatePicker } from '../components/ui/DatePicker';
import { Checkbox } from '../components/ui/Checkbox';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { LineItemRow } from '../components/domain/LineItemRow';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { QuoteApprovalModal } from '../components/domain/QuoteApprovalModal';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { calculateSubtotal, calculateTaxAmount, calculateInvoiceTotal, calculateProfitMargin } from '../utils/calculations';
import { LineItem, InvoiceStatus } from '../types';
import { ArrowLeftIcon, PlusIcon, SaveIcon, SendIcon, CreditCardIcon, CheckCircleIcon, AlertCircleIcon, CalendarIcon, UserIcon, RefreshCwIcon, FileTextIcon, MailIcon, TrashIcon, XCircleIcon, SettingsIcon, ExternalLinkIcon } from 'lucide-react';
import { Modal } from '../components/ui/Modal';
import { INVOICE_FILTERS_STORAGE_KEY } from './Invoices';
interface LineItemFormData {
  description: string;
  quantity: number;
  price: number;
  jobCost?: number;
  isTaxable: boolean;
}
// API invoice item type
interface ApiInvoiceItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable?: number;
}
// API invoice response type
interface ApiInvoiceResponse {
  id: string;
  number: string;
  items: ApiInvoiceItem[];
  amount: number;
  paid_amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  shipping_amount: number;
  discount: number;
  status: string;
  created: number;
  sent_date?: number;
  invoice_date?: number;
  customer_id: string;
  customer_name: string;
  subscription_id?: string;
  subscription_number?: string;
  payment_period_days: number;
  payment_date?: number;
  send_options?: string;
  link?: string;
}
export function InvoiceForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const isExistingInvoice = !!id;
  // Check if we're on the edit route (for draft/future invoices)
  const isEditMode = location.pathname.endsWith('/edit');
  // Show view mode only for existing invoices that are NOT in edit mode
  const isViewMode = isExistingInvoice && !isEditMode;
  const {
    createInvoice,
    updateInvoice,
    getInvoiceById,
    getLineItemsByInvoiceId,
    selectedCompanyId,
    taxRate
  } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQuoteApprovalModal, setShowQuoteApprovalModal] = useState(false);
  // Re-send invoice modal state
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendViaEmail, setResendViaEmail] = useState(true);
  const [resendViaSMS, setResendViaSMS] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Delete invoice modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Mark as paid state
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  // Void invoice state
  const [isVoiding, setIsVoiding] = useState(false);
  // Payments modal state
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  // Invoice data from API (for viewing)
  const [invoiceData, setInvoiceData] = useState<ApiInvoiceResponse | null>(null);
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [sentDate, setSentDate] = useState<Date | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  const [createdDate, setCreatedDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<string>('draft');
  const [shippingCharge, setShippingCharge] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [fee, setFee] = useState(0);
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaSMS, setSendViaSMS] = useState(false);
  const [paymentPeriodDays, setPaymentPeriodDays] = useState(30);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [subscriptionNumber, setSubscriptionNumber] = useState<string | null>(null);
  const [invoiceLink, setInvoiceLink] = useState<string | null>(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [storedAmount, setStoredAmount] = useState(0);
  const [storedTax, setStoredTax] = useState(0);
  const [storedTaxLabel, setStoredTaxLabel] = useState<string | undefined>(undefined);
  const [storedFee, setStoredFee] = useState(0);
  const [storedFeeLabel, setStoredFeeLabel] = useState<string | undefined>(undefined);
  // Extra settings for labels
  const [taxLineLabel, setTaxLineLabel] = useState('');
  const [feeLineLabel, setFeeLineLabel] = useState('Fee');
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Get today's date for minimum date restriction
  const today = new Date().toISOString().split('T')[0];
  // Check if selected date is in the future (not today)
  const isFutureDate = useMemo(() => {
    if (!date) return false;
    const selectedDate = new Date(date);
    const todayDate = new Date(today);
    // Compare dates without time
    selectedDate.setHours(0, 0, 0, 0);
    todayDate.setHours(0, 0, 0, 0);
    return selectedDate > todayDate;
  }, [date, today]);
  // Load existing invoice data from API
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!isExistingInvoice || !id) return;
      setIsLoadingInvoice(true);
      setLoadError(null);
      try {
        console.log('Fetching invoice:', id);
        const response = await callBillingAPI('getInvoice', {
          id
        });
        console.log('Invoice API response:', response);
        setInvoiceData(response);
        // Populate form fields
        setCustomerId(response.customer_id || '');
        setCustomerName(response.customer_name || '');
        setInvoiceNumber(response.number || '');
        setStatus(response.status || 'draft');
        setShippingCharge(parseFloat(response.shipping_amount) || 0);
        setDiscount(parseFloat(response.discount) || 0);
        setFee(parseFloat(response.fee) || 0);
        setPaymentPeriodDays(parseInt(response.payment_period_days) || 30);
        setPaidAmount(parseFloat(response.paid_amount) || 0);
        setStoredAmount(parseFloat(response.amount) || 0);
        setStoredTax(parseFloat(response.tax) || 0);
        setStoredTaxLabel(response.tax_label || undefined);
        setStoredFee(parseFloat(response.fee) || 0);
        setStoredFeeLabel(response.fee_label || undefined);
        // Set label fields from API response
        if (response.tax_label) {
          setTaxLineLabel(response.tax_label);
        }
        if (response.fee_label) {
          setFeeLineLabel(response.fee_label);
        }
        if (response.created) {
          setCreatedDate(new Date(response.created * 1000));
        }
        // Set the date field from invoice_date (priority) or fallback to created date
        if (response.invoice_date) {
          const invoiceDateObj = new Date(response.invoice_date * 1000);
          setDate(invoiceDateObj.toISOString().split('T')[0]);
        } else if (response.created) {
          setDate(new Date(response.created * 1000).toISOString().split('T')[0]);
        }
        if (response.sent_date) {
          setSentDate(new Date(response.sent_date * 1000));
        }
        if (response.payment_date) {
          setPaymentDate(new Date(response.payment_date * 1000));
        }
        if (response.subscription_id) {
          setSubscriptionId(response.subscription_id);
          setSubscriptionNumber(response.subscription_number || null);
        }
        if (response.link) {
          setInvoiceLink(response.link);
        }
        // Set send options based on API response
        if (response.send_options) {
          const sendOptions = response.send_options;
          if (sendOptions === 'email') {
            setSendViaEmail(true);
            setSendViaSMS(false);
          } else if (sendOptions === 'sms') {
            setSendViaEmail(false);
            setSendViaSMS(true);
          } else if (sendOptions === 'email_sms') {
            setSendViaEmail(true);
            setSendViaSMS(true);
          }
        }
        // Transform items
        if (response.items && Array.isArray(response.items)) {
          const transformedItems: LineItemFormData[] = response.items.map((item: ApiInvoiceItem) => ({
            description: item.title,
            quantity: parseFloat(String(item.qty)) || 1,
            price: parseFloat(String(item.price)) || 0,
            isTaxable: item.taxable === 1
          }));
          setLineItems(transformedItems.length > 0 ? transformedItems : [{
            description: '',
            quantity: 1,
            price: 0,
            isTaxable: true
          }]);
        }
      } catch (error) {
        console.error('Failed to fetch invoice:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load invoice');
      } finally {
        setIsLoadingInvoice(false);
      }
    };
    fetchInvoice();
  }, [isExistingInvoice, id]);
  // Calculate totals
  const calculatedLineItems: LineItem[] = useMemo(() => {
    return lineItems.map((item, index) => ({
      id: `temp-${index}`,
      invoice_id: id || 'new',
      description: item.description,
      quantity: item.quantity,
      price: item.price,
      job_cost: item.jobCost,
      is_taxable: item.isTaxable,
      profit_margin: calculateProfitMargin(item.price, item.jobCost)
    }));
  }, [lineItems, id]);
  // Calculate subtotal from line items (works for both new and existing invoices)
  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = parseFloat(String(item.quantity)) || 0;
      const price = parseFloat(String(item.price)) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);
  // Calculate taxable subtotal (only items marked as taxable)
  const taxableSubtotal = useMemo(() => {
    return lineItems.filter(item => item.isTaxable).reduce((sum, item) => {
      const qty = parseFloat(String(item.quantity)) || 0;
      const price = parseFloat(String(item.price)) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);
  const taxAmount = useMemo(() => isViewMode ? storedTax : calculateTaxAmount(calculatedLineItems, taxRate), [isViewMode, storedTax, calculatedLineItems, taxRate]);
  const total = useMemo(() => isViewMode ? storedAmount : calculateInvoiceTotal(parseFloat(String(subtotal)) || 0, parseFloat(String(taxAmount)) || 0, parseFloat(String(shippingCharge)) || 0, parseFloat(String(discount)) || 0) + (parseFloat(String(fee)) || 0), [isViewMode, storedAmount, subtotal, taxAmount, shippingCharge, discount, fee]);
  const balanceDue = useMemo(() => total - paidAmount, [total, paidAmount]);
  // Get default tax label based on company tax rate
  const getDefaultTaxLabel = useCallback(() => {
    const taxPercent = taxRate * 100;
    if (taxPercent > 0) {
      return `Tax (${taxPercent.toFixed(0)}%)`;
    }
    return 'Tax';
  }, [taxRate]);
  // Initialize default tax label when creating new invoice
  useEffect(() => {
    if (!isExistingInvoice && !taxLineLabel) {
      setTaxLineLabel(getDefaultTaxLabel());
    }
  }, [isExistingInvoice, taxLineLabel, getDefaultTaxLabel]);
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, updates: Partial<LineItemFormData>) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customer = 'Please select a customer';
    }
    if (!date) {
      newErrors.date = 'Please select a date';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }
    const hasEmptyLineItem = lineItems.some(item => !item.description.trim() || item.price <= 0);
    if (hasEmptyLineItem) {
      newErrors.lineItems = 'Please fill in all line item details';
    }
    // Require at least one send option
    if (!sendViaEmail && !sendViaSMS) {
      newErrors.sendOptions = 'Please select at least one send option';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async (sendAfterSave: boolean = false) => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      if (isExistingInvoice && id) {
        // Update existing invoice via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Format date as mm-dd-yyyy
        const dateObj = new Date(date);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedInvoiceDate = `${month}-${day}-${year}`;
        // Determine send_options value
        let sendOptions: string;
        if (sendViaEmail && sendViaSMS) {
          sendOptions = 'email_sms';
        } else if (sendViaEmail) {
          sendOptions = 'email';
        } else {
          sendOptions = 'sms';
        }
        const apiParams: Record<string, any> = {
          id: id,
          customer_id: customerId,
          items: items,
          total_amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          },
          shipping_amount: shippingCharge,
          discount: discount,
          term_days: paymentPeriodDays,
          invoice_date: formattedInvoiceDate,
          send_options: sendOptions
        };
        // Add send flag if "Save & Send" was clicked
        if (sendAfterSave) {
          apiParams.send = 1;
        }
        console.log('Updating invoice with params:', apiParams);
        const updatedInvoice = await callBillingAPI('editInvoice', apiParams);
        console.log('Update invoice response:', updatedInvoice);
      } else {
        // Create new invoice via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Format date as mm-dd-yyyy
        const dateObj = new Date(date);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedInvoiceDate = `${month}-${day}-${year}`;
        // Determine send_options value
        let sendOptions: string;
        if (sendViaEmail && sendViaSMS) {
          sendOptions = 'email_sms';
        } else if (sendViaEmail) {
          sendOptions = 'email';
        } else {
          sendOptions = 'sms';
        }
        const apiParams: Record<string, any> = {
          company_id: selectedCompanyId,
          customer_id: customerId,
          items: items,
          total_amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          },
          shipping_amount: shippingCharge,
          discount: discount,
          term_days: paymentPeriodDays,
          invoice_date: formattedInvoiceDate,
          send_options: sendOptions
        };
        // Add send flag if "Save & Send" was clicked
        if (sendAfterSave) {
          apiParams.send = 1;
        }
        console.log('Creating invoice with params:', apiParams);
        const newInvoice = await callBillingAPI('createInvoice', apiParams);
        console.log('Create invoice response:', newInvoice);
        const newInvoiceId = newInvoice.id;
        console.log('New invoice ID:', newInvoiceId);
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save invoice'
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Get status badge
  const getStatusBadge = (statusValue: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft'
      },
      sent: {
        variant: 'info',
        label: 'Sent'
      },
      paid: {
        variant: 'success',
        label: 'Paid'
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial Paid'
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue'
      },
      voided: {
        variant: 'default',
        label: 'Voided'
      },
      future: {
        variant: 'info',
        label: 'Future'
      }
    };
    const config = statusConfig[statusValue] || {
      variant: 'default' as const,
      label: statusValue
    };
    return <Badge variant={config.variant} data-id="element-1145">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Handle re-send invoice
  const handleResendInvoice = async () => {
    if (!resendViaEmail && !resendViaSMS) {
      setResendError('Please select at least one send option');
      return;
    }
    if (!id) return;
    setIsResending(true);
    setResendError(null);
    try {
      // Determine send_options value
      let sendOptions: string;
      if (resendViaEmail && resendViaSMS) {
        sendOptions = 'email_sms';
      } else if (resendViaEmail) {
        sendOptions = 'email';
      } else {
        sendOptions = 'sms';
      }
      await callBillingAPI('sendInvoice', {
        id: id,
        send_options: sendOptions
      });
      setShowResendModal(false);
      // Optionally refresh the page or show success message
      window.location.reload();
    } catch (error) {
      console.error('Failed to re-send invoice:', error);
      setResendError(error instanceof Error ? error.message : 'Failed to re-send invoice');
    } finally {
      setIsResending(false);
    }
  };
  // Open re-send modal with current send options pre-selected
  const openResendModal = () => {
    setResendViaEmail(sendViaEmail);
    setResendViaSMS(sendViaSMS);
    setResendError(null);
    setShowResendModal(true);
  };
  // Handle delete invoice
  const handleDeleteInvoice = async () => {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await callBillingAPI('deleteInvoice', {
        id: id
      });
      setShowDeleteModal(false);
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
    }
  };
  // Handle mark invoice as paid
  const handleMarkAsPaid = async () => {
    if (!id) return;
    setIsMarkingPaid(true);
    try {
      await callBillingAPI('markInvoicePaid', {
        id: id
      });
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to mark invoice as paid'
      });
    } finally {
      setIsMarkingPaid(false);
    }
  };
  // Handle void invoice
  const handleVoidInvoice = async () => {
    if (!id) return;
    setIsVoiding(true);
    try {
      await callBillingAPI('voidInvoice', {
        id: id
      });
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to void invoice:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to void invoice'
      });
    } finally {
      setIsVoiding(false);
    }
  };
  // Fetch payments for the invoice
  const fetchPayments = async () => {
    if (!id) return;
    setIsLoadingPayments(true);
    setPaymentsError(null);
    try {
      const response = await callBillingAPI('getPayments', {
        invoice_id: id
      });
      // Handle different response structures
      let paymentsList: any[] = [];
      if (Array.isArray(response)) {
        paymentsList = response;
      } else if (response.payments && Array.isArray(response.payments)) {
        paymentsList = response.payments;
      } else if (response.data && Array.isArray(response.data)) {
        paymentsList = response.data;
      }
      setPayments(paymentsList);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPaymentsError(error instanceof Error ? error.message : 'Failed to load payments');
    } finally {
      setIsLoadingPayments(false);
    }
  };
  // Open payments modal and fetch payments
  const openPaymentsModal = () => {
    setShowPaymentsModal(true);
    fetchPayments();
  };
  // Format payment date
  const formatPaymentDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const existingInvoice = isExistingInvoice && id ? getInvoiceById(id) : null;
  // Navigate back to invoices list with preserved filters
  const handleBackToInvoices = () => {
    const savedFilters = sessionStorage.getItem(INVOICE_FILTERS_STORAGE_KEY);
    if (savedFilters) {
      navigate(`/invoices?${savedFilters}`);
    } else {
      navigate('/invoices');
    }
  };
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'quote',
    label: 'Quote'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'approved',
    label: 'Approved'
  }, {
    value: 'refunded',
    label: 'Refunded'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  // Payment terms options (days until overdue)
  const paymentTermsOptions = [{
    value: '1',
    label: '1 day'
  }, {
    value: '2',
    label: '2 days'
  }, {
    value: '3',
    label: '3 days'
  }, {
    value: '5',
    label: '5 days'
  }, {
    value: '10',
    label: '10 days'
  }, {
    value: '15',
    label: '15 days'
  }, {
    value: '30',
    label: '30 days'
  }];
  // Line items table columns for viewing
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    render: (item: LineItemFormData) => <span className="font-medium text-gray-900" data-id="element-1146">{item.description}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: LineItemFormData) => item.quantity
  }, {
    key: 'price',
    header: 'Price',
    render: (item: LineItemFormData) => formatCurrency(item.price)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: LineItemFormData) => formatCurrency(item.quantity * item.price)
  }, {
    key: 'taxable',
    header: 'Taxable',
    render: (item: LineItemFormData) => <Badge variant={item.isTaxable ? 'success' : 'default'} data-id="element-1147">
          {item.isTaxable ? 'Yes' : 'No'}
        </Badge>
  }];
  // Loading state for editing
  if (isExistingInvoice && isLoadingInvoice) {
    return <div className="space-y-6" data-id="element-1148">
        <div className="flex items-center gap-4" data-id="element-1149">
          <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1151" />} data-id="element-1150">
            Back
          </Button>
        </div>
        <Card data-id="element-1152">
          <div className="p-12 text-center" data-id="element-1153">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1154"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-1155">Loading invoice...</p>
          </div>
        </Card>
      </div>;
  }
  // Error state for editing
  if (isExistingInvoice && loadError) {
    return <div className="space-y-6" data-id="element-1156">
        <div className="flex items-center gap-4" data-id="element-1157">
          <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1159" />} data-id="element-1158">
            Back
          </Button>
        </div>
        <Card data-id="element-1160">
          <div className="p-12 text-center" data-id="element-1161">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1162">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1163" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1164">
              Failed to load invoice
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1165">{loadError}</p>
            <Button size="sm" onClick={() => window.location.reload()} data-id="element-1166">
              Retry
            </Button>
          </div>
        </Card>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1167">
      {/* Page Header */}
      <div className="flex items-center gap-4" data-id="element-1168">
        <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1170" />} data-id="element-1169">
          Back
        </Button>
        <div className="flex-1" data-id="element-1171">
          <div className="flex items-center gap-3" data-id="element-1172">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1173">
              {isExistingInvoice ? isEditMode ? `Edit Invoice #${invoiceNumber || ''}` : invoiceNumber ? `Invoice #${invoiceNumber}` : 'Invoice Details' : 'New Invoice'}
            </h1>
            {isExistingInvoice && getStatusBadge(status)}
          </div>
        </div>
        {isExistingInvoice && invoiceLink && <a href={invoiceLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors" data-id="element-1174">
            <ExternalLinkIcon className="w-4 h-4" data-id="element-1175" />
            Open Invoice
          </a>}
        {isViewMode && (status === 'sent' || status === 'overdue' || status === 'paid' || status === 'partial_paid' || status === 'voided') && <Button variant="secondary" onClick={openPaymentsModal} leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1177" />} data-id="element-1176">
              Payments
            </Button>}
        {isViewMode && existingInvoice && existingInvoice.status === 'quote' && <Button onClick={() => setShowQuoteApprovalModal(true)} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1179" />} data-id="element-1178">
              Approve Quote
            </Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1180">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1181">
          {/* Error message */}
          {errors.submit && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-1182">
              <p className="text-sm text-red-600" data-id="element-1183">{errors.submit}</p>
            </div>}

          {/* Invoice Details */}
          <Card data-id="element-1184">
            <CardHeader title="Invoice Details" data-id="element-1185" />
            {isViewMode ?
          // View mode for existing invoice
          <div className="space-y-4" data-id="element-1186">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1187">
                  <div data-id="element-1188">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1189">
                      Invoice Number
                    </label>
                    <p className="text-gray-900 font-medium" data-id="element-1190">
                      {invoiceNumber || '—'}
                    </p>
                  </div>
                  <div data-id="element-1191">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1192">
                      Customer
                    </label>
                    <div className="flex items-center gap-2" data-id="element-1193">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-1194" />
                      <p className="text-gray-900 font-medium" data-id="element-1195">
                        {customerName || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1196">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1197">
                      Created
                    </label>
                    <div className="flex items-center gap-2" data-id="element-1198">
                      <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-1199" />
                      <p className="text-gray-900" data-id="element-1200">
                        {createdDate ? formatDate(createdDate) : '—'}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1201">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1202">
                      Payment Terms
                    </label>
                    <p className="text-gray-900" data-id="element-1203">
                      {paymentPeriodDays}{' '}
                      {paymentPeriodDays === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                  {sentDate && <div data-id="element-1204">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1205">
                        Sent Date
                      </label>
                      <p className="text-gray-900" data-id="element-1206">{formatDate(sentDate)}</p>
                    </div>}
                  {paymentDate && <div data-id="element-1207">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1208">
                        Payment Date
                      </label>
                      <p className="text-green-600 font-medium" data-id="element-1209">
                        {formatDate(paymentDate)}
                      </p>
                    </div>}
                  <div data-id="element-1210">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1211">
                      Status
                    </label>
                    {getStatusBadge(status)}
                  </div>
                  {subscriptionId && <div data-id="element-1212">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1213">
                        Subscription
                      </label>
                      <Link to={`/subscriptions/${subscriptionId}`} className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1" data-id="element-1214">
                        <RefreshCwIcon className="w-4 h-4" data-id="element-1215" />
                        {subscriptionNumber || subscriptionId.slice(0, 8)}
                      </Link>
                    </div>}
                </div>
              </div> :
          // Edit mode for new or editable invoice
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1216">
                <CustomerSelect value={customerId} onChange={setCustomerId} error={errors.customer} selectedLabel={customerName} data-id="element-1217" />
                <DatePicker label="Invoice Date" value={date} onChange={e => setDate(e.target.value)} error={errors.date} data-id="element-1218" />
                <Select label="Payment Terms" options={paymentTermsOptions} value={String(paymentPeriodDays)} onChange={e => setPaymentPeriodDays(parseInt(e.target.value))} helperText="Days until invoice becomes overdue" data-id="element-1219" />
              </div>}
          </Card>

          {/* Line Items */}
          <Card padding={isViewMode ? 'none' : undefined} data-id="element-1220">
            <div className={isViewMode ? 'p-6 border-b border-gray-200' : ''} data-id="element-1221">
              <CardHeader title="Line Items" action={!isViewMode && <Button variant="secondary" size="sm" onClick={addLineItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1224" />} data-id="element-1223">
                      Add Item
                    </Button>} data-id="element-1222" />
            </div>
            {errors.lineItems && !isViewMode && <p className="text-sm text-red-600 mb-4" data-id="element-1225">{errors.lineItems}</p>}
            {isViewMode ? <Table columns={lineItemColumns} data={lineItems} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-1226" /> : <div className="space-y-4" data-id="element-1227">
                {lineItems.map((item, index) => <LineItemRow key={index} index={index} description={item.description} quantity={item.quantity} price={item.price} jobCost={item.jobCost} isTaxable={item.isTaxable} onDescriptionChange={value => updateLineItem(index, {
              description: value
            })} onQuantityChange={value => updateLineItem(index, {
              quantity: value
            })} onPriceChange={value => updateLineItem(index, {
              price: value
            })} onJobCostChange={value => updateLineItem(index, {
              jobCost: value
            })} onTaxableChange={value => updateLineItem(index, {
              isTaxable: value
            })} onRemove={() => removeLineItem(index)} canRemove={lineItems.length > 1} data-id="element-1228" />)}
              </div>}
          </Card>

          {/* Additional Charges (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1229">
              <CardHeader title="Additional Charges" data-id="element-1230" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1231">
                <Input label="Shipping Charge" type="number" min="0" step="0.01" value={shippingCharge} onChange={e => setShippingCharge(parseFloat(e.target.value) || 0)} data-id="element-1232" />
                <Input label="Discount" type="number" min="0" step="0.01" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} data-id="element-1233" />
                <Input label="Fee" type="number" min="0" step="0.01" value={fee} onChange={e => setFee(parseFloat(e.target.value) || 0)} data-id="element-1234" />
              </div>
            </Card>}

          {/* Extra Settings (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1235">
              <CardHeader title="Extra Settings" data-id="element-1236" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1237">
                <Input label="Tax Line Label" value={taxLineLabel} onChange={e => setTaxLineLabel(e.target.value)} placeholder={getDefaultTaxLabel()} helperText="Label shown for tax in invoice summary" data-id="element-1238" />
                <Input label="Fee Line Label" value={feeLineLabel} onChange={e => setFeeLineLabel(e.target.value)} placeholder="Fee" helperText="Label shown for fee in invoice summary" data-id="element-1239" />
              </div>
            </Card>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-1240">
          {/* Totals */}
          <Card data-id="element-1241">
            <CardHeader title="Invoice Summary" data-id="element-1242" />
            <TotalsPanel subtotal={subtotal} taxAmount={taxAmount} taxRate={isViewMode ? taxableSubtotal > 0 ? storedTax / taxableSubtotal : 0 : taxRate} taxLabel={isViewMode ? storedTaxLabel : taxLineLabel} shippingCharge={shippingCharge} discount={discount} fee={isViewMode ? storedFee : fee} feeLabel={isViewMode ? storedFeeLabel : feeLineLabel} total={total} data-id="element-1243" />
            {isViewMode && paidAmount > 0 && <div className="mt-4 pt-4 border-t border-gray-200 space-y-2" data-id="element-1244">
                <div className="flex justify-between text-sm" data-id="element-1245">
                  <span className="text-gray-500" data-id="element-1246">Paid Amount</span>
                  <span className="text-green-600 font-medium" data-id="element-1247">
                    {formatCurrency(paidAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold" data-id="element-1248">
                  <span className="text-gray-900" data-id="element-1249">Balance Due</span>
                  <span className={balanceDue > 0 ? 'text-red-600' : 'text-green-600'} data-id="element-1250">
                    {formatCurrency(balanceDue)}
                  </span>
                </div>
              </div>}
          </Card>

          {/* Send Options (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1251">
              <CardHeader title="Send Options" data-id="element-1252" />
              <div className="space-y-3" data-id="element-1253">
                <Checkbox label="Send via Email" checked={sendViaEmail} onChange={e => setSendViaEmail(e.target.checked)} data-id="element-1254" />
                <Checkbox label="Send via SMS" checked={sendViaSMS} onChange={e => setSendViaSMS(e.target.checked)} data-id="element-1255" />
                {errors.sendOptions && <p className="text-sm text-red-600" data-id="element-1256">{errors.sendOptions}</p>}
              </div>
            </Card>}

          {/* Send Options (view mode - disabled) */}
          {isViewMode && <Card data-id="element-1257">
              <CardHeader title="Send Options" data-id="element-1258" />
              <div className="space-y-3" data-id="element-1259">
                <Checkbox label="Send via Email" checked={sendViaEmail} disabled data-id="element-1260" />
                <Checkbox label="Send via SMS" checked={sendViaSMS} disabled data-id="element-1261" />
              </div>
            </Card>}

          {/* Re-Send Invoice Button (only for sent or overdue invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue') && <Button className="w-full" variant="secondary" onClick={openResendModal} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1263" />} data-id="element-1262">
              Re-Send Invoice
            </Button>}

          {/* Mark as Paid Button (only for sent, overdue, or partial_paid invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue' || status === 'partial_paid') && <Button className="w-full" onClick={handleMarkAsPaid} isLoading={isMarkingPaid} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1265" />} data-id="element-1264">
                Mark as Paid
              </Button>}

          {/* Void Invoice Button (only for sent or overdue invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue') && <Button className="w-full" variant="danger" onClick={handleVoidInvoice} isLoading={isVoiding} leftIcon={<XCircleIcon className="w-4 h-4" data-id="element-1267" />} data-id="element-1266">
              Void Invoice
            </Button>}

          {/* Actions (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1268">
              <div className="space-y-3" data-id="element-1269">
                <Button className="w-full" onClick={() => handleSave(false)} isLoading={isLoading} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1271" />} data-id="element-1270">
                  {isExistingInvoice ? 'Save Changes' : 'Save Draft'}
                </Button>
                <Button variant="secondary" className="w-full" onClick={() => handleSave(true)} isLoading={isLoading} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1273" />} data-id="element-1272">
                  Save & Send
                </Button>
              </div>
            </Card>}

          {/* Delete Invoice Button (only for draft or future invoices in edit mode) */}
          {isEditMode && (status === 'draft' || status === 'future') && <Button className="w-full" variant="danger" onClick={() => setShowDeleteModal(true)} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1275" />} data-id="element-1274">
              Delete Invoice
            </Button>}
        </div>
      </div>

      {/* Payment Modal */}
      {existingInvoice && existingInvoice.status !== 'quote' && <PaymentModal invoice={existingInvoice} isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} data-id="element-1276" />}

      {/* Quote Approval Modal */}
      {existingInvoice && existingInvoice.status === 'quote' && <QuoteApprovalModal invoice={existingInvoice} lineItems={calculatedLineItems} isOpen={showQuoteApprovalModal} onClose={() => setShowQuoteApprovalModal(false)} onApproved={() => {
      setShowQuoteApprovalModal(false);
      navigate('/invoices');
    }} data-id="element-1277" />}

      {/* Re-Send Invoice Modal */}
      <Modal isOpen={showResendModal} onClose={() => setShowResendModal(false)} title="Re-Send Invoice" size="sm" data-id="element-1278">
        <div className="space-y-4" data-id="element-1279">
          <p className="text-sm text-gray-600" data-id="element-1280">
            Select how you would like to re-send this invoice to the customer.
          </p>
          <div className="space-y-3" data-id="element-1281">
            <Checkbox label="Send via Email" checked={resendViaEmail} onChange={e => setResendViaEmail(e.target.checked)} data-id="element-1282" />
            <Checkbox label="Send via SMS" checked={resendViaSMS} onChange={e => setResendViaSMS(e.target.checked)} data-id="element-1283" />
          </div>
          {resendError && <p className="text-sm text-red-600" data-id="element-1284">{resendError}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1285">
            <Button variant="secondary" onClick={() => setShowResendModal(false)} disabled={isResending} data-id="element-1286">
              Cancel
            </Button>
            <Button onClick={handleResendInvoice} isLoading={isResending} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1288" />} data-id="element-1287">
              Re-Send
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Invoice Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Invoice" size="sm" data-id="element-1289">
        <div className="space-y-4" data-id="element-1290">
          <p className="text-gray-600" data-id="element-1291">
            Are you sure you want to delete this invoice? This action cannot be
            undone.
          </p>
          {deleteError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1292">
              <p className="text-sm text-red-600" data-id="element-1293">{deleteError}</p>
            </div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1294">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} data-id="element-1295">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteInvoice} isLoading={isDeleting} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1297" />} data-id="element-1296">
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payments Modal */}
      <Modal isOpen={showPaymentsModal} onClose={() => setShowPaymentsModal(false)} title="Payment History" size="lg" data-id="element-1298">
        <div className="space-y-4" data-id="element-1299">
          {isLoadingPayments ? <div className="p-8 text-center" data-id="element-1300">
              <div className="inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1301"></div>
              <p className="mt-3 text-sm text-gray-500" data-id="element-1302">Loading payments...</p>
            </div> : paymentsError ? <div className="p-6 text-center" data-id="element-1303">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-red-100 rounded-full mb-3" data-id="element-1304">
                <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1305" />
              </div>
              <p className="text-sm text-gray-900 font-medium mb-1" data-id="element-1306">
                Failed to load payments
              </p>
              <p className="text-sm text-gray-500 mb-3" data-id="element-1307">{paymentsError}</p>
              <Button size="sm" onClick={fetchPayments} data-id="element-1308">
                Retry
              </Button>
            </div> : payments.length === 0 ? <div className="p-8 text-center" data-id="element-1309">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3" data-id="element-1310">
                <CreditCardIcon className="w-6 h-6 text-gray-400" data-id="element-1311" />
              </div>
              <p className="text-sm text-gray-500" data-id="element-1312">
                No payments recorded for this invoice.
              </p>
            </div> : <div className="divide-y divide-gray-200" data-id="element-1313">
              {payments.map((payment, index) => <div key={index} className="py-4 first:pt-0 last:pb-0" data-id="element-1314">
                  <div className="flex items-center justify-between" data-id="element-1315">
                    <div className="flex items-center gap-3" data-id="element-1316">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payment.status === 'success' ? 'bg-green-100' : 'bg-red-100'}`} data-id="element-1317">
                        <CreditCardIcon className={`w-5 h-5 ${payment.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1318" />
                      </div>
                      <div data-id="element-1319">
                        <div className="flex items-center gap-2" data-id="element-1320">
                          <span className="font-medium text-gray-900" data-id="element-1321">
                            {payment.card_brand || 'Card'} ••••{' '}
                            {payment.card_last_digits || '****'}
                          </span>
                          <Badge variant={payment.status === 'success' ? 'success' : 'danger'} data-id="element-1322">
                            {payment.status === 'success' ? 'Success' : 'Failed'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-1323">
                          {payment.card_exp ? `Exp: ${payment.card_exp}` : ''}
                          {payment.created ? ` • ${formatPaymentDate(payment.created)}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`text-lg font-semibold ${payment.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1324">
                      {formatCurrency(payment.amount || 0)}
                    </span>
                  </div>
                </div>)}
            </div>}
          <div className="flex justify-end pt-4 border-t border-gray-200" data-id="element-1325">
            <Button variant="secondary" onClick={() => setShowPaymentsModal(false)} data-id="element-1326">
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}